import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Card, Grid } from '@mui/material';
import 'leaflet/dist/leaflet.css';

import { apiKey } from '../../common/config';
import { getClosestCoordinateIndex, getFileNameWithSuffix } from '../../utils/utils';
import './styles.css';

const INITIAL_CENTER_LATITUDE = 39.520139;
const INITIAL_CENTER_LONGITUDE = 44.671443;

const markerIcon = new L.divIcon({
	html: '<div class="map-position-marker"><div class="map-position-marker__ring"></div><div class="map-position-marker__dot"></div></div>',
	className: '',
	iconSize: [32, 32],
	iconAnchor: [16, 16],
});

const MapEvents = ({ center, setZoom, zoom, onMapClick, initialZoomedIn, setInitialZoomedIn }) => {
	const map = useMapEvents({
		zoomend(e) {
			setZoom(e.target.getZoom());
		},
		click: onMapClick,
	});

	useEffect(() => {
		if (initialZoomedIn || center[0] === INITIAL_CENTER_LATITUDE) {
			map.panTo(center);
		} else {
			map.flyTo(center, 18, { duration: 2 });
			setInitialZoomedIn(true);
		}
	}, [center]);

	useEffect(() => {
		if (initialZoomedIn) {
			map.setZoom(zoom);
		}
	}, [zoom]);

	return null;
};

const InvalidateOnResize = () => {
	const map = useMap();
	useEffect(() => {
		const container = map.getContainer();
		const observer = new ResizeObserver(() => {
			map.invalidateSize();
		});
		observer.observe(container);
		return () => observer.disconnect();
	}, [map]);
	return null;
};

const CustomMap = ({
	csvURL,
	updateCSVDataReadingState,
	csvDataRead,
	currentImageFileName,
	onClickMarker,
	currentImageIndex,
	onCoordinateChange,
}) => {
	const [center, setCenter] = useState([INITIAL_CENTER_LATITUDE, INITIAL_CENTER_LONGITUDE]);
	const [zoom, setZoom] = useState(3);
	const [timeAndCoordinates, setTimeAndCoordinates] = useState([]);
	const [polylineSegments, setPolylineSegments] = useState([]);
	const [initialZoomedIn, setInitialZoomedIn] = useState(false);

	useEffect(() => {
		if (csvURL && !csvDataRead) {
			if (zoom !== 18) {
				setInitialZoomedIn(false);
			}
			readCSV(csvURL);
		}
	}, [csvURL]);

	const readCSV = async (url) => {
		var text = '';
		var xhr = new XMLHttpRequest();
		xhr.open('GET', url);
		xhr.onload = () => {
			if (xhr.status === 200) {
				text = xhr.response;
				analyzeCSVdata(text);
			}
			text = xhr.response;
		};
		xhr.send();
		updateCSVDataReadingState(true);
	};

	const analyzeCSVdata = (text) => {
		const timeAndCoordinatesRead = [];
		const rows = text.split('\n');
		for (let i = 1; i < rows.length; i += 1) {
			const currentRow = rows[i].split('\t');
			if (currentRow.length >= 4) {
				const timeAndCoordinate = {
					gpsSeconds: parseFloat(currentRow[0]),
					fileName: getFileNameWithSuffix(currentRow[1]),
					latitude: parseFloat(currentRow[2]),
					longitude: parseFloat(currentRow[3]),
				};
				timeAndCoordinatesRead.push(timeAndCoordinate);
			}
		}
		setTimeAndCoordinates(timeAndCoordinatesRead);

		const THRESHOLD_M = 20;
		const toRad = d => d * Math.PI / 180;
		const haversineM = (lat1, lon1, lat2, lon2) => {
			const R = 6371000;
			const dLat = toRad(lat2 - lat1);
			const dLon = toRad(lon2 - lon1);
			const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
			return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		};
		const segments = [];
		let current = [timeAndCoordinatesRead[0]];
		for (let i = 1; i < timeAndCoordinatesRead.length; i++) {
			const prev = timeAndCoordinatesRead[i - 1];
			const curr = timeAndCoordinatesRead[i];
			if (haversineM(prev.latitude, prev.longitude, curr.latitude, curr.longitude) > THRESHOLD_M) {
				segments.push(current);
				current = [curr];
			} else {
				current.push(curr);
			}
		}
		segments.push(current);
		setPolylineSegments(segments);
	};

	useEffect(() => {
		if (timeAndCoordinates.length > 0) {
			// Use current image index if available, otherwise default to first coordinate
			const targetIndex = currentImageIndex >= 0 && currentImageIndex < timeAndCoordinates.length 
				? currentImageIndex 
				: 0;
			const targetCoordinate = timeAndCoordinates[targetIndex];
			setCenter([targetCoordinate.latitude, targetCoordinate.longitude]);
			if (onCoordinateChange) {
				onCoordinateChange(targetCoordinate);
			}
		}
	}, [timeAndCoordinates, currentImageIndex]);

	useEffect(() => {
		if (timeAndCoordinates.length !== 0) {
			const currentCoordinate = timeAndCoordinates[currentImageIndex];
			setCenter([currentCoordinate.latitude, currentCoordinate.longitude]);
			if (onCoordinateChange) {
				onCoordinateChange(currentCoordinate);
			}
		}
	}, [currentImageFileName]);

	const onMapClick = (e) => {
		const clickedCoordinate = [e.latlng.lat, e.latlng.lng];

		const { coordinate, index } = getClosestCoordinateIndex(clickedCoordinate, timeAndCoordinates);

		setCenter([coordinate.latitude, coordinate.longitude]);
		onClickMarker(index);
		if (onCoordinateChange) {
			onCoordinateChange(coordinate);
		}
	};

	return (
		<Grid className="custom-map__container">
			<Card className={`custom-map__upload-map-component ${csvURL ? '' : 'custom-map__blurred'}`}>
				<MapContainer
					center={center}
					zoom={zoom}
					className="custom-map__custom-map"
					onZoom={(e) => setZoom(e.target.getZoom())}
					style={{
						height: '100%',
						width: '100%',
					}}
					attributionControl={false}
					onMapClick={onMapClick}
				>
					<InvalidateOnResize />
					<TileLayer url={`https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png?apikey=${apiKey}`} />
					{polylineSegments.map((segment, idx) => (
						<Polyline
							key={idx}
							positions={segment.map((tc) => [tc.latitude, tc.longitude])}
							weight={4}
							color="#1a73ba"
						/>
					))}
					{timeAndCoordinates.length !== 0 && (
						<>
							<Marker position={center} icon={markerIcon} />
							<MapEvents
								center={center}
								setZoom={setZoom}
								zoom={zoom}
								onMapClick={onMapClick}
								initialZoomedIn={initialZoomedIn}
								setInitialZoomedIn={setInitialZoomedIn}
							/>
						</>
					)}
				</MapContainer>
			</Card>
		</Grid>
	);
};

export default CustomMap;
